import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronLeft, ChevronRight, Phone, Users, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
];

const HOURS_START = 8; // 8 AM
const HOURS_END = 20;  // 8 PM

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  return new Date(d.setDate(diff));
}

function formatDateStr(d) {
  return d.toISOString().split('T')[0];
}

function getWeekDays(weekStart) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

function generateTimeSlots(durationMinutes) {
  const slots = [];
  for (let h = HOURS_START; h < HOURS_END; h++) {
    for (let m = 0; m < 60; m += durationMinutes) {
      if (h === HOURS_END - 1 && m + durationMinutes > 60) break;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
}

function formatTime12(t) {
  const [h, m] = t.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h12}:${m} ${ampm}`;
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const InterviewSlotsTab = () => {
  const [interviewType, setInterviewType] = useState('phone');
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [slots, setSlots] = useState([]);
  const [durations, setDurations] = useState({ phone_duration: 30, in_person_duration: 60 });
  const [loading, setLoading] = useState(true);
  const [savingDuration, setSavingDuration] = useState(false);

  const currentDuration = interviewType === 'phone' ? durations.phone_duration : durations.in_person_duration;
  const weekDays = getWeekDays(weekStart);
  const timeSlots = generateTimeSlots(currentDuration);

  const fetchSlots = useCallback(async () => {
    try {
      const ws = formatDateStr(weekStart);
      const res = await axios.get(`${API}/interview-scheduling/availability`, {
        params: { interview_type: interviewType, week_start: ws }
      });
      setSlots(res.data);
    } catch { /* ignore */ }
  }, [weekStart, interviewType]);

  const fetchDurations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/interview-scheduling/duration-settings`);
      setDurations(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSlots(), fetchDurations()]).finally(() => setLoading(false));
  }, [fetchSlots, fetchDurations]);

  const updateDuration = async (val) => {
    const numVal = parseInt(val);
    const field = interviewType === 'phone' ? 'phone_duration' : 'in_person_duration';
    setDurations(prev => ({ ...prev, [field]: numVal }));
    setSavingDuration(true);
    try {
      await axios.put(`${API}/interview-scheduling/duration-settings`, { [field]: numVal });
      toast.success('Duration updated');
      fetchSlots();
    } catch {
      toast.error('Failed to save duration');
    } finally {
      setSavingDuration(false);
    }
  };

  const findSlot = (date, time) => {
    const dateStr = formatDateStr(date);
    return slots.find(s => s.date === dateStr && s.start_time === time);
  };

  const toggleSlot = async (date, time) => {
    const dateStr = formatDateStr(date);
    const existing = findSlot(date, time);

    if (existing) {
      if (existing.booked_by_candidate_id) {
        toast.error('Cannot remove a booked slot');
        return;
      }
      // Delete
      try {
        await axios.delete(`${API}/interview-scheduling/availability/${existing.id}`);
        setSlots(prev => prev.filter(s => s.id !== existing.id));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to remove slot');
      }
    } else {
      // Create
      try {
        const res = await axios.post(`${API}/interview-scheduling/availability`, {
          interview_type: interviewType,
          date: dateStr,
          start_time: time
        });
        setSlots(prev => [...prev, res.data]);
      } catch (err) {
        if (err.response?.status !== 409) {
          toast.error(err.response?.data?.detail || 'Failed to add slot');
        }
      }
    }
  };

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };
  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };
  const goToday = () => setWeekStart(getWeekStart(new Date()));

  const weekLabel = `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const availableCount = slots.filter(s => s.is_available && !s.booked_by_candidate_id).length;
  const bookedCount = slots.filter(s => s.booked_by_candidate_id).length;

  return (
    <Card data-testid="interview-availability-tab">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle>Interview Availability</CardTitle>
            <CardDescription>Set your available times for candidate interviews</CardDescription>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-400 inline-block" /> Available ({availableCount})</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-400 inline-block" /> Booked ({bookedCount})</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Interview Type Tabs + Duration */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex gap-2">
            <Button
              variant={interviewType === 'phone' ? 'default' : 'outline'}
              className={interviewType === 'phone' ? 'bg-[#4B2D6E] hover:bg-[#3a2357]' : ''}
              onClick={() => setInterviewType('phone')}
              data-testid="type-phone-btn"
            >
              <Phone className="h-4 w-4 mr-2" /> Phone Interview
            </Button>
            <Button
              variant={interviewType === 'in_person' ? 'default' : 'outline'}
              className={interviewType === 'in_person' ? 'bg-[#4B2D6E] hover:bg-[#3a2357]' : ''}
              onClick={() => setInterviewType('in_person')}
              data-testid="type-in-person-btn"
            >
              <Users className="h-4 w-4 mr-2" /> In-Person Interview
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#666]" />
            <Label className="text-sm text-[#666]">Slot Duration:</Label>
            <Select
              value={String(currentDuration)}
              onValueChange={updateDuration}
              disabled={savingDuration}
            >
              <SelectTrigger className="w-28" data-testid="duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={prevWeek} data-testid="prev-week-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-[#4B2D6E] text-lg">{weekLabel}</h3>
            <Button variant="outline" size="sm" onClick={goToday} className="text-xs">Today</Button>
          </div>
          <Button variant="ghost" size="icon" onClick={nextWeek} data-testid="next-week-btn">
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Calendar Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-3 border-[#4B2D6E] border-t-transparent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[700px]">
              {/* Header row */}
              <div className="grid grid-cols-8 gap-0 mb-1">
                <div className="p-2 text-xs text-[#999] font-medium text-center">Time</div>
                {weekDays.map((day, i) => {
                  const isToday = formatDateStr(day) === formatDateStr(new Date());
                  return (
                    <div
                      key={i}
                      className={`p-2 text-center rounded-t-lg ${isToday ? 'bg-[#4B2D6E]/10' : ''}`}
                    >
                      <p className={`text-xs font-semibold ${isToday ? 'text-[#4B2D6E]' : 'text-[#666]'}`}>
                        {DAY_NAMES[i]}
                      </p>
                      <p className={`text-sm font-bold ${isToday ? 'text-[#4B2D6E]' : 'text-[#333]'}`}>
                        {day.getDate()}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Time rows */}
              <div className="border rounded-lg overflow-hidden">
                {timeSlots.map((time, rowIdx) => (
                  <div
                    key={time}
                    className={`grid grid-cols-8 gap-0 ${rowIdx > 0 ? 'border-t border-gray-100' : ''}`}
                  >
                    <div className="p-1.5 text-xs text-[#999] text-center flex items-center justify-center border-r border-gray-100 bg-gray-50/50">
                      {formatTime12(time)}
                    </div>
                    {weekDays.map((day, colIdx) => {
                      const existing = findSlot(day, time);
                      const isBooked = existing?.booked_by_candidate_id;
                      const isAvailable = existing && !isBooked;
                      const isPast = formatDateStr(day) < formatDateStr(new Date());

                      return (
                        <button
                          key={colIdx}
                          onClick={() => !isPast && toggleSlot(day, time)}
                          disabled={isPast}
                          className={`p-1.5 min-h-[36px] transition-all border-r border-gray-100 last:border-r-0
                            ${isBooked
                              ? 'bg-amber-100 cursor-not-allowed'
                              : isAvailable
                              ? 'bg-emerald-100 hover:bg-emerald-200 cursor-pointer'
                              : isPast
                              ? 'bg-gray-50 cursor-not-allowed opacity-50'
                              : 'bg-white hover:bg-emerald-50 cursor-pointer'
                            }`}
                          title={
                            isBooked ? 'Booked' : isAvailable ? 'Click to remove' : isPast ? 'Past' : 'Click to add'
                          }
                          data-testid={`cell-${formatDateStr(day)}-${time}`}
                        >
                          {isBooked && (
                            <Badge className="bg-amber-400 text-white text-[10px] px-1 py-0">Booked</Badge>
                          )}
                          {isAvailable && (
                            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mx-auto" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-[#999] text-center">
          Click any empty cell to mark it as available. Click a green cell to remove it. Booked slots cannot be removed.
        </p>
      </CardContent>
    </Card>
  );
};

export { InterviewSlotsTab };
